import type { Object3D } from 'three/webgpu';
import {
  AssemblyComponentFixationState,
} from '../component-fixation';
import {
  applyComponentInstanceSnapshotToObject,
  type ComponentInstanceSnapshot,
} from '../component-instance';
import type {
  AssemblyComponentHistorySnapshot,
  AssemblyHistoryStore,
} from '../../history/domain';

/**
 * 装配体场景历史 Store 创建输入。
 */
export interface CreateAssemblySceneHistoryStoreInput {
  /** 根据组件实例 ID 或场景对象 ID 解析 Object3D。 */
  readonly resolveObject: (componentInstanceId: string, snapshot: AssemblyComponentHistorySnapshot) => Object3D | null;
  //------ 装配体引用复制历史 开始------
  /** 当 redo 创建引用复制组件时，根据快照生成 Object3D。 */
  readonly createObject?: (snapshot: AssemblyComponentHistorySnapshot) => Object3D | null;
  /** 当 undo 删除引用复制组件时，移除 Object3D 与宿主主状态。 */
  readonly removeObject?: (componentInstanceId: string) => void;
  //------ 装配体引用复制历史 结束------
  /** 应用快照后请求渲染。 */
  readonly requestRender?: () => void;
  /** 应用快照后触发装配派生引用重算。 */
  readonly recomputeAssemblyReferences?: () => void;
  /** 应用快照后同步宿主维护的组件主状态投影。 */
  readonly syncComponentState?: (snapshot: AssemblyComponentHistorySnapshot) => void;
  /** 应用快照后直接同步该对象关联的实例矩阵槽。 */
  readonly syncComponentTransform?: (object: Object3D) => void;
}

/**
 * 将 history adapter 快照转换为组件实例快照。
 *
 * @param snapshot history adapter 快照。
 * @returns 组件实例快照。
 */
function convertAssemblyHistorySnapshotToComponentInstanceSnapshot(
  snapshot: AssemblyComponentHistorySnapshot,
): ComponentInstanceSnapshot {
  return {
    componentInstanceId: snapshot.componentInstanceId,
    assemblyId: snapshot.assemblyId,
    sourceNodeId: snapshot.sourceNodeId ?? snapshot.componentInstanceId,
    sourcePartFileUuid: snapshot.sourcePartFileUuid,
    parentComponentInstanceId: snapshot.parentComponentInstanceId,
    transform: snapshot.transform,
    fixationState: snapshot.fixationState === 'fixed'
      ? AssemblyComponentFixationState.Fixed
      : AssemblyComponentFixationState.Floating,
    suppressed: snapshot.suppressed ?? false,
  };
}

/**
 * 创建直接作用于 Three.js 场景对象的装配体历史 Store。
 *
 * 设计说明：
 * 该 Store 是 PatchApplier 与 Viewer 场景之间的边界适配层。history adapter 仍然只认识
 * `AssemblyHistoryStore` 抽象，不直接依赖 Viewer、web-client 或浏览器存储。
 *
 * @param input 创建输入。
 * @returns 装配体历史 Store。
 */
export function createAssemblySceneHistoryStore(
  input: CreateAssemblySceneHistoryStoreInput,
): AssemblyHistoryStore {
  return {
    upsertComponentState(snapshot) {
      //------ 装配体引用复制历史 开始------
      const object = input.resolveObject(snapshot.componentInstanceId, snapshot)
        ?? input.createObject?.(snapshot)
        ?? null;
      //------ 装配体引用复制历史 结束------
      if (!object) {
        throw new Error(`未找到装配体组件实例对应的场景对象：${snapshot.componentInstanceId}`);
      }

      applyComponentInstanceSnapshotToObject(
        object,
        convertAssemblyHistorySnapshotToComponentInstanceSnapshot(snapshot),
      );
      input.syncComponentTransform?.(object);
      input.recomputeAssemblyReferences?.();
      input.syncComponentState?.(snapshot);
      input.requestRender?.();
    },
    deleteComponentState(componentInstanceId) {
      //------ 装配体引用复制历史 开始------
      if (!input.removeObject) {
        throw new Error(`当前装配体组件历史不支持删除组件实例回放：${componentInstanceId}`);
      }
      input.removeObject(componentInstanceId);
      input.recomputeAssemblyReferences?.();
      input.requestRender?.();
      //------ 装配体引用复制历史 结束------
    },
  };
}
